/**
 * @file suppliedQty.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-30
 * @modified 2026-10-22
 * @description When Add/Update Supplied Qty (or, on a third-party order,
 *              Update Delivery) is available — the client's copy of
 *              orderDeliveryService.suppliedQtyBlock, so the option is greyed
 *              out with the reason before anyone clicks it. The server refuses
 *              the same cases.
 *
 *   Not before the delivery day. And, where the order's POs drive its delivery
 *   status, not before it has been despatched — a stock-only order and a
 *   third-party one have no PO to say so and are exempt from that part.
 *
 * 1.1.0 (2026-10-22, Cam): a third-party order from our stock (`collects`) is
 *   collected by the haulier before its delivery date, and Update Delivery
 *   records that collection — so it is never "not yet" for that order. The
 *   server decides which orders (orderDeliveryService.deliveryState). The
 *   collected status 'Despatched' counts as despatched.
 */

const THIRD_PARTY = 'Third-Party Delivery';
const DESPATCHED = ['Despatched', 'In-Transit', 'Partially Delivered', 'Delivered'];

const todayLondon = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
const ukDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/**
 * @param {{ deliveryDate, deliveryStatus, terms, hasSupplyPos, collects }} o
 *        collects — a third-party order from our stock (Update Delivery
 *        records the haulier's collection before the delivery date)
 * @returns {string|null} why not, or null when it is available
 */
export function suppliedQtyBlock({ deliveryDate, deliveryStatus, terms, hasSupplyPos, collects = false }) {
  if (collects && terms === THIRD_PARTY) return null;
  const date = deliveryDate ? String(deliveryDate).slice(0, 10) : null;
  if (!date) return 'The order has no delivery date yet';
  if (date > todayLondon()) return `Delivery is not until ${ukDate(date)} — recorded once it is due`;
  const exempt = terms === THIRD_PARTY || !hasSupplyPos;
  if (!exempt && !DESPATCHED.includes(deliveryStatus)) {
    return `Delivery status is "${deliveryStatus || 'Not Despatched'}" — recorded once it has been despatched`;
  }
  return null;
}

export const isThirdParty = (terms) => terms === THIRD_PARTY;

/**
 * Collected from our stock by a third-party haulier? A search row knows
 * `collects_from_stock` (the server's rule) or, failing that, that nothing
 * on it needs buying in (`needs_supply_po === false`).
 */
export const collectsFromStock = (order) => !!order && order.excel_agri_delivery_terms === THIRD_PARTY
  && (order.collects_from_stock === true || (order.collects_from_stock == null && order.needs_supply_po === false));

/** "Collected 21/10/2026" for a collected date ('YYYY-MM-DD'), else ''. */
export const collectedText = (day) => {
  const d = day ? String(day).slice(0, 10) : '';
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? `Collected ${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '';
};
